import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  Typography,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useNotification } from "@refinedev/core";
import { exportBatch } from "../../../api/batches";
import {
  JobStatus,
  statusToLabel,
  BatchStats,
  Batch,
  ClientJobStatus,
} from "../../../pages/playground/types";
import { useAuthStore } from "../../../authProvider";
import { downloadHelper } from "../../../utils/download";

type Column = {
  id: string;
  label: string;
};

type ExportDialogProps = {
  open: boolean;
  onClose: () => void;
  brandName: string;
  batch: Batch;
  stats?: BatchStats;
  hasPendingTranslations?: boolean;
};

const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  brandName,
  batch,
  stats,
  hasPendingTranslations = false,
}) => {
  const { user } = useAuthStore();
  const hasLaraLanguages = (batch?.settings?.lara_languages?.length ?? 0) > 0;

  const { open: openNotification } = useNotification();
  const [isExporting, setIsExporting] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState<JobStatus[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);
  const [exportFormat, setExportFormat] = useState<"csv" | "xlsx">("csv");

  // Available statuses for export (excluding NOT_READY, TO_BE_STARTED, IN_PROGRESS)
  const availableStatuses: JobStatus[] = useMemo(
    () =>
      user?.isClientRelated
        ? (Object.values(ClientJobStatus).filter(
            (status) => status !== ClientJobStatus.NOT_READY
          ) as unknown as JobStatus[])
        : (Object.values(JobStatus).filter(
            (status) => status !== JobStatus.NOT_READY
          ) as JobStatus[]),
    [user]
  );

  // Available columns for export (non-client users only see this dialog)
  const availableColumns = useMemo<Column[]>(() => {
    const baseColumns: Column[] = [
      { id: "status", label: "Current Job Status" },
      { id: "copywriters_reviewers", label: "Copywriters & Reviewers" },
      { id: "notes", label: "Notes for Client" },
      { id: "feedback", label: "Feedback from Client" },
    ];

    // Add translations column if lara_languages is configured
    if (hasLaraLanguages) {
      baseColumns.push({ id: "translations", label: "AI Translations" });
    }

    return baseColumns;
  }, [hasLaraLanguages]);

  // Initialize selected statuses and columns when opening the dialog
  useEffect(() => {
    if (open) {
      // Default to all available statuses
      setSelectedStatuses([...availableStatuses]);
      // Default to no additional columns selected
      setSelectedColumns([]);
    }
  }, [open, availableStatuses]);

  // Handle status checkbox toggle
  const handleStatusToggle = (status: JobStatus) => {
    setSelectedStatuses((prev) => {
      if (prev.includes(status)) {
        return prev.filter((s) => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Handle column checkbox toggle
  const handleColumnToggle = (columnId: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(columnId)) {
        return prev.filter((c) => c !== columnId);
      } else {
        return [...prev, columnId];
      }
    });
  };

  // Calculate how many jobs will be exported based on selected statuses
  const getSelectedJobCount = () => {
    if (!stats) return 0;

    return selectedStatuses.reduce((total, status) => {
      return total + (stats.jobs_by_status[status] || 0);
    }, 0);
  };

  // Handle export action
  const handleExport = async () => {
    setIsExporting(true);

    try {
      await downloadHelper(
        () =>
          exportBatch({
            batchId: String(batch.id),
            statuses: selectedStatuses,
            columns: selectedColumns,
            format: exportFormat,
          }),
        {
          fallbackFilename: `${brandName}_${batch.name}_export`,
          format: exportFormat,
        }
      );

      // Success notification
      if (openNotification) {
        openNotification({
          message: "Export successful",
          description: "The file has been downloaded",
          type: "success",
        });
      }
    } catch (error) {
      // Error notification
      if (openNotification) {
        openNotification({
          message: "Export failed",
          description:
            error instanceof Error
              ? error.message
              : "An unknown error occurred",
          type: "error",
        });
      }
      console.error("Export error:", error);
    } finally {
      setIsExporting(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export</DialogTitle>
      <DialogContent>
        {/* Status selection */}
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Export SKUs with these states:
          </Typography>
          <FormGroup row sx={{ gap: 0.5 }}>
            {availableStatuses.map((status) => (
              <FormControlLabel
                key={status}
                control={
                  <Checkbox
                    checked={selectedStatuses.includes(status)}
                    onChange={() => handleStatusToggle(status)}
                    size="small"
                  />
                }
                label={statusToLabel(status)}
                sx={{ minWidth: "140px" }}
              />
            ))}
          </FormGroup>
        </Box>

        {/* Warning for pending translations */}
        {hasPendingTranslations &&
          hasLaraLanguages &&
          user?.isClientRelated && (
            <Alert severity="warning" sx={{ mb: 1, py: 0.5 }}>
              <Typography variant="body2">
                Some jobs are still being translated. Export may have incomplete translations.
              </Typography>
            </Alert>
          )}

        {/* Column selection */}
        <Box>
          <Typography variant="body2" fontWeight={500} gutterBottom>
            Columns:
          </Typography>
          <FormGroup row sx={{ gap: 0.5 }}>
            <FormControlLabel
              control={<Checkbox checked disabled size="small" />}
              label="Fashionwords Data"
              sx={{ minWidth: "140px" }}
            />
            {availableColumns.map((column) => (
              <FormControlLabel
                key={column.id}
                control={
                  <Checkbox
                    checked={selectedColumns.includes(column.id)}
                    onChange={() => handleColumnToggle(column.id)}
                    size="small"
                  />
                }
                label={column.label}
                sx={{ minWidth: "140px" }}
              />
            ))}
          </FormGroup>
        </Box>

      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ToggleButtonGroup
            value={exportFormat}
            exclusive
            onChange={(_, value) => value && setExportFormat(value)}
            size="small"
          >
            <ToggleButton value="csv">CSV</ToggleButton>
            <ToggleButton value="xlsx">XLSX</ToggleButton>
          </ToggleButtonGroup>
          <Button
            onClick={handleExport}
            variant="contained"
            color="primary"
            disabled={
              selectedStatuses.length === 0 ||
              isExporting ||
              getSelectedJobCount() === 0
            }
          >
            {isExporting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              "Export"
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ExportDialog;
